import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { Spinner } from '../components/Spinner';
import { useToast } from '../lib/toast';

export function LoginPage() {
  const { signIn, user, loading } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();
  const [params] = useSearchParams();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const isSignup = params.get('signup') === '1';

  useEffect(() => {
    if (!loading && user) navigate('/app', { replace: true });
  }, [loading, user, navigate]);

  return (
    <div className="min-h-screen flex">
      <div className="hidden lg:flex flex-1 items-center justify-center bg-gradient-to-br from-brand-600 to-violet-700 text-white px-12">
        <div className="max-w-md">
          <Link to="/" className="inline-flex items-center gap-2 text-white/90 mb-12">
            <span className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-white/15 backdrop-blur">
              S
            </span>
            <span className="text-lg font-semibold">SLS Short</span>
          </Link>
          <h1 className="text-4xl font-bold tracking-tight">Short links worth measuring.</h1>
          <p className="mt-4 text-white/80">
            Branded shortlinks, dynamic QR codes, password-protected destinations, and detailed
            analytics — fast, secure, and built for teams.
          </p>
        </div>
      </div>
      <div className="flex flex-1 items-center justify-center px-6 py-12 bg-white dark:bg-ink-950">
        <div className="w-full max-w-sm">
          <Link to="/" className="lg:hidden inline-flex items-center gap-2 mb-8 font-semibold">
            <span className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-gradient-to-br from-brand-500 to-violet-600 text-white">
              S
            </span>
            SLS Short
          </Link>
          <h2 className="text-2xl font-bold tracking-tight">
            {isSignup ? 'Create an account' : 'Sign in to your workspace'}
          </h2>
          <p className="mt-2 text-sm text-ink-500 dark:text-ink-400">
            {isSignup
              ? 'Self-service signup is coming soon. Ask your administrator to provision an account.'
              : 'Enter your credentials to access the dashboard.'}
          </p>

          <form
            className="mt-8 space-y-4"
            onSubmit={async (e) => {
              e.preventDefault();
              setError(null);
              setSubmitting(true);
              try {
                await signIn(email.trim(), password);
                toast.success('Signed in');
                navigate('/app', { replace: true });
              } catch (err) {
                setError(err instanceof Error ? err.message : 'Sign in failed.');
              } finally {
                setSubmitting(false);
              }
            }}
          >
            {error && (
              <div className="rounded-lg bg-red-50 dark:bg-red-950/40 text-red-700 dark:text-red-200 px-3 py-2 text-sm">
                {error}
              </div>
            )}
            <div>
              <label className="label" htmlFor="email">
                Email address
              </label>
              <input
                id="email"
                type="email"
                autoComplete="email"
                required
                className="input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
            <div>
              <label className="label" htmlFor="password">
                Password
              </label>
              <input
                id="password"
                type="password"
                autoComplete="current-password"
                required
                className="input"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </div>
            <button type="submit" className="btn-primary w-full py-2.5" disabled={submitting}>
              {submitting && <Spinner size={16} />}
              {isSignup ? 'Continue' : 'Sign in'}
            </button>
          </form>

          {isSignup ? (
            <p className="mt-6 text-sm text-ink-500 dark:text-ink-400">
              Already have an account?{' '}
              <Link to="/login" className="text-brand-600 dark:text-brand-300 font-medium">
                Sign in
              </Link>
            </p>
          ) : (
            <p className="mt-6 text-sm text-ink-500 dark:text-ink-400">
              Need an account?{' '}
              <Link to="/login?signup=1" className="text-brand-600 dark:text-brand-300 font-medium">
                Get started
              </Link>
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
